import { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ChevronDown } from 'lucide-react'
import { Button } from 'app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { QuickCreateTaskModal } from 'app/components/tasks/QuickCreateTaskModal'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { closeModal, showModal } from 'app/lib/modal'
import { sendAtLabel } from 'app/lib/scheduledSend'
import { notifyError } from 'app/services/notify.client'
import type { LocationJson } from 'app/types/entities'
import { logError } from 'lib/logging'
import { BulkActionPanel } from './BulkActionPanel'
import { BulkProgressBar } from './BulkProgressBar'
import { BulkResultsModal } from './BulkResultsModal'
import { BulkSendSmsModal } from './BulkSendSmsModal'
import { useSmsSchedulingSelection } from './useSmsSchedulingSelection'
import type { BulkAction, BulkActionBarProps, BulkActionValue, BulkProgress, BulkRowOutcome } from './types'

/**
 * Docked bar shown while the table is in selection mode. Surfaces the selected
 * count, a select-all-on-page toggle, the generated action menu, and the active
 * action's value panel. Applies via the consumer-supplied config.
 *
 * observer: reads the scheduled-sms feature toggle in render; useFeatureToggle
 * resolves false -> real asynchronously and its contract requires an observer
 * wrapper (TRK-6020). The component is generic, and observer() erases the type
 * parameter, so the reactive export is cast back to the generic signature.
 */
function BulkActionBarInner<T>({ selection, config, onExit }: BulkActionBarProps<T>) {
  const [activeAction, setActiveAction] = useState<BulkAction | null>(null)
  const [applying, setApplying] = useState(false)
  const [progress, setProgress] = useState<BulkProgress | null>(null)
  const [results, setResults] = useState<{
    title: string
    outcomes: BulkRowOutcome[]
    /** The run was queued rather than performed, so the modal says so instead
     *  of reporting the rows as sent. */
    scheduled?: boolean
    sendsAt?: string
    /** A run-level warning that isn't any one row's fault (see BulkApplyResult). */
    caveat?: string
  } | null>(null)
  const [confirm] = useConfirmation()

  const count = selection.rows.length

  const applyAction = async (action: BulkAction, value: BulkActionValue) => {
    const rows = selection.rows
    setApplying(true)
    // Seed at zero so the bar appears with the run's total on the first frame,
    // instead of waiting for the first row to land.
    setProgress({ done: 0, total: rows.length, ok: 0, failed: 0, skipped: 0 })
    // The task form is an app-level modal that would cover the docked bar. Dismiss it
    // on submit so the progress bar carries the run, the way the other actions do.
    if (action.control === `task`) closeModal()
    try {
      const { outcomes, scheduledSendAtLocal, caveat } = await config.apply(action.id, value, rows, (next) =>
        setProgress(next),
      )
      // Dismiss the task modal (opened via showModal) if it was the source; a no-op
      // for the inline-panel/SMS paths where no app-level modal is open.
      closeModal()
      // Show the per-contact outcomes. Keep the selection: updated rows stay pinned
      // at the top showing their new values; failed rows keep their prior values.
      // A queued run gets its own title and vocabulary: reporting 400 scheduled
      // texts as "400 succeeded" tells the user they have been sent.
      setResults({
        title: scheduledSendAtLocal ? `${action.label}: scheduled, not sent yet` : `${action.label}: results`,
        outcomes,
        scheduled: !!scheduledSendAtLocal,
        // `pickerTimezone`, not `timezone`: for a selection spanning practices
        // `timezone` is undefined and the run would report itself as scheduled
        // with no time at all, having just asked the user to pick one. The label
        // is a wall clock every recipient gets on their own clock, so the
        // representative zone the picker used prints the right hour.
        sendsAt: scheduledSendAtLocal && pickerTimezone ? sendAtLabel(scheduledSendAtLocal, pickerTimezone) : undefined,
        caveat,
      })
      setActiveAction(null)
    } catch (err) {
      // config.apply reports per-row failures in its outcomes; reaching here means the
      // whole apply died before producing any. Surface it and restore the action menu —
      // without this, the rejection is unhandled and the bar strands on the action label.
      logError(`Bulk action "${action.label}" failed`, err)
      notifyError(`${action.label} failed`)
      setActiveAction(null)
    } finally {
      setApplying(false)
      setProgress(null)
    }
  }

  const handleApply = (value: BulkActionValue) => {
    if (activeAction) return applyAction(activeAction, value)
  }

  // Create task opens the quick task form in bulk mode. It's presented via the app's `showModal`
  // (headlessui) rather than a Radix Dialog: the due-date CalendarPicker portals to
  // <body>, and Radix's focus trap steals focus from it — the same reason every
  // other task modal in the app uses showModal.
  const openTaskModal = (action: BulkAction) => {
    showModal(
      <QuickCreateTaskModal
        bulkCount={count}
        onBulkSubmit={(value) => applyAction(action, value)}
        onTaskCreatedOrUpdated={() => {}}
      />,
      {
        // The quick form's title input is its header, so no Modal title bar.
        hideTitle: true,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  // A `confirm` action collects no value — it's destructive, so it goes straight to the
  // app's danger dialog and applies on submit. `activeAction` is set inside the callback
  // rather than before opening: useConfirmationAlert only ever resolves (cancelling leaves
  // the promise pending), so setting it up front would strand the bar in an action state
  // the user couldn't back out of. An apply failure is handled inside applyAction itself.
  const openConfirmAction = (action: BulkAction) => {
    confirm({
      variant: `danger`,
      title: action.confirmTitle ?? action.label,
      description: action.confirmDescription?.(count) ?? ``,
    }).then(() => {
      setActiveAction(action)
      return applyAction(action, ``)
    })
  }

  // Send SMS collects its value in a modal that covers the docked bar, so that modal
  // owns both the composer and the progress row. Every branch that depends on which
  // surface is in play reads this one flag, so the two can never disagree about it.
  const smsComposerOpen = activeAction?.control === `sms`
  // A confirm or none action collects no value, so it never shows the inline panel either. Its
  // progress renders in the docked bar, which the danger dialog does not cover.
  const valuelessRunning = activeAction?.control === `confirm` || activeAction?.control === `none`

  // SMS is only offered for contact-shaped rows, which carry locationId; other
  // row types simply count zero. Fetched only while the SMS modal is open.
  const selectedOriginals = useMemo(
    () => selection.rows as { locationId?: string | null }[],
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [selection.rows],
  )
  const { quietSkipCount, timezone } = useSmsSchedulingSelection(selectedOriginals, smsComposerOpen)
  // The scheduled run resolves each contact's own practice clock (see
  // useContactBulkActions), so a selection spanning timezones is no longer a
  // reason to withhold the picker; that restriction only existed while a
  // scheduled send stored one shared instant for the whole selection.
  const scheduledSmsEnabled = useFeatureToggle(`scheduled-sms`)
  const scheduleAvailable = scheduledSmsEnabled && count > 0

  // The picker only needs A clock to show times on, not the correct one for
  // every recipient (each contact resolves its own at apply time, and one whose
  // clock rejects the picked time is skipped and reported), so the first selected
  // location's timezone stands in when the selection spans more than one. Fetched
  // only while the SMS modal is open, and only as a fallback: `timezone` above
  // already covers the common single-practice case. `BulkSendSmsModal`'s
  // `timezone` prop documents this as the representative-clock contract.
  const firstLocationId = selectedOriginals[0]?.locationId ?? undefined
  const [firstLocationTimezone, setFirstLocationTimezone] = useState<string | undefined>()
  useEffect(() => {
    if (!smsComposerOpen || !firstLocationId || timezone) {
      setFirstLocationTimezone(undefined)
      return
    }
    let cancelled = false
    gqlFetchOne<LocationJson>(`location`, firstLocationId)
      .then((location) => {
        if (!cancelled) setFirstLocationTimezone(location?.timezone ?? undefined)
      })
      .catch((err) => logError(`Bulk SMS schedule picker could not load location ${firstLocationId}`, err))
    return () => {
      cancelled = true
    }
  }, [smsComposerOpen, firstLocationId, timezone])
  const pickerTimezone = timezone ?? firstLocationTimezone

  // The chosen action's value collector renders as its own row rather than
  // stretching the bar horizontally. SMS/task/confirm/none use their own surfaces, not the panel.
  const showValueRow = !!activeAction && !smsComposerOpen && !valuelessRunning

  return (
    <div className="absolute bottom-[calc(1rem+env(safe-area-inset-bottom))] left-3 right-3 z-30 flex flex-col gap-2.5 rounded-lg border border-default bg-surface-raised px-4 py-2.5 shadow-xl md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2">
      <div
        className={`${showValueRow ? `hidden md:flex` : `flex`} flex-wrap items-center gap-2 md:flex-nowrap md:gap-4`}
      >
        <span className="hidden text-ui-sm font-semibold text-strong md:inline">{count} selected</span>

        {!activeAction ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" disabled={count === 0}>
                Choose action <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {config.actions.map((action) => (
                <DropdownMenuItem
                  key={action.id}
                  onSelect={() => {
                    if (action.control === `task`) return openTaskModal(action)
                    if (action.control === `confirm`) return openConfirmAction(action)
                    setActiveAction(action)
                    if (action.control === `none`) return applyAction(action, ``)
                  }}
                >
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : smsComposerOpen || valuelessRunning ? (
          // These run from their own surface (SMS composer, danger dialog) or straight from the
          // menu, rather than the inline panel, so the bar just names the action. The progress row below carries
          // the run itself.
          <span className="text-ui-sm font-medium text-default">{activeAction.label}</span>
        ) : null}

        {!showValueRow && (
          <div className="ml-auto flex items-center gap-3">
            {!activeAction && (
              <Button variant="subtle" size="inline" className="text-ui-sm" onClick={selection.clear}>
                Clear
              </Button>
            )}
            <Button variant="subtle" size="inline" className="text-ui-sm" onClick={onExit} disabled={applying}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {showValueRow && activeAction && (
        <div className="flex items-center border-t border-subtle pt-2.5">
          <BulkActionPanel
            action={activeAction}
            count={count}
            applying={applying}
            onApply={handleApply}
            onCancel={() => setActiveAction(null)}
            onExit={onExit}
          />
        </div>
      )}

      {progress && !smsComposerOpen && (
        <div className="border-t border-subtle pt-2.5 md:w-96">
          <BulkProgressBar progress={progress} verb="Applying" />
        </div>
      )}

      <BulkResultsModal
        open={!!results}
        title={results?.title ?? ``}
        outcomes={results?.outcomes ?? []}
        scheduled={results?.scheduled}
        sendsAt={results?.sendsAt}
        caveat={results?.caveat}
        onClose={() => setResults(null)}
      />

      {smsComposerOpen && activeAction && (
        <BulkSendSmsModal
          open
          count={count}
          applying={applying}
          progress={progress}
          variables={activeAction.variables}
          quietSkipCount={quietSkipCount}
          timezone={pickerTimezone}
          scheduleAvailable={scheduleAvailable}
          onApply={handleApply}
          onCancel={() => setActiveAction(null)}
        />
      )}
    </div>
  )
}

export const BulkActionBar = observer(BulkActionBarInner) as typeof BulkActionBarInner
